import { NumeralThousandGroupStyles, formatNumeral } from 'cleave-zen';
import { ChangeEvent } from 'react';
import { Control, FieldValues, Path, useController } from 'react-hook-form';
import styled from 'styled-components';

import { Input, Label } from '@/components/Forms';
import ValidationMessage from '@/components/Forms/ValidationMessage';
import Text from '@/components/Text';

const InputWrap = styled.div`
  position: relative;
`;

const InputLabel = styled(Text).attrs({
  as: 'span',
  $type: 'captionSemibold',
  $color: 'gray',
})`
  position: absolute;
  top: 11px;
  left: 48px;
`;

const Buttons = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  width: 46px;
  height: 100%;
  display: flex;
  flex-direction: column;
  border-left: 1px solid ${({ theme }) => theme.colors.grayscale};
`;

const Button = styled.button.attrs({ type: 'button' })`
  color: ${({ theme }) => theme.colors.black};
  flex: 1;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 4px 0;

  &:nth-child(2) {
    align-items: flex-start;
  }
  &:hover {
    color: ${({ theme }) => theme.colors.gray};
  }
`;

interface Props<T extends FieldValues> {
  control: Control<T>;
  label: string;
  inputLabel: string;
  name: Path<T>;
}

const NumericInput = <T extends FieldValues>({
  control,
  name,
  label,
  inputLabel,
}: Props<T>) => {
  const {
    field: { value, onChange },
    fieldState: { error },
  } = useController<T>({
    name,
    control,
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(
      formatNumeral(e.target.value, {
        numeralPositiveOnly: true,
        stripLeadingZeroes: true,
        numeralDecimalScale: 0,
        numeralThousandsGroupStyle: NumeralThousandGroupStyles.NONE,
      }),
    );
  };

  const decrement = () => {
    if (!value) {
      return;
    }

    if (value === '1') {
      return;
    }

    onChange((parseInt(value) - 1).toString());
  };

  const increment = () => {
    if (!value) {
      return;
    }

    onChange((parseInt(value) + 1).toString());
  };

  return (
    <div>
      <Label>{label}</Label>
      <InputWrap>
        <Input name="font_size" value={value} onChange={handleChange} />
        <InputLabel>{inputLabel}</InputLabel>
        <Buttons>
          <Button onClick={increment}>
            <svg
              width="8"
              height="5"
              viewBox="0 0 8 5"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M4 0L0 5H8L4 0Z" fill="currentColor" />
            </svg>
          </Button>
          <Button onClick={decrement}>
            <svg
              width="8"
              height="5"
              viewBox="0 0 8 5"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M4 5L8 0H0L4 5Z" fill="currentColor" />
            </svg>
          </Button>
        </Buttons>
      </InputWrap>
      {!!error && <ValidationMessage>{error.message}</ValidationMessage>}
    </div>
  );
};

export default NumericInput;
